import type { AppliedBy } from "@unbox/api";
import { Plus, ReverseLeft, Ruler } from "@untitledui/icons";
import { useMemo } from "react";
import type { useBlockedCreatorsList } from "@/api/blocklist";
import { UnboxTable } from "@/components/application/table/unbox-table";
import type { UnboxTableColumn, UnboxTableProps } from "@/components/application/table/unbox-table.types";
import { Avatar } from "@/components/base/avatar/avatar";
import { CreatorCohortCellContent } from "@/components/creator-cohorts/creator-cohort-cell";
import { RuleSourceTooltip } from "@/components/rules/rule-source-tooltip";
import { useCurrency } from "@/hooks/use-currency";
import { CreatorSafetyStatusBadge } from "@/pages/collaborations/creator-safety-status-badge";
import { TrendNumberCellContent } from "@/pages/explorers/columns/shared-cells";
import { getCreatorName } from "@/utils/creator-name";

export type BlockedCreatorRow = NonNullable<ReturnType<typeof useBlockedCreatorsList>["data"]>["items"][number];

const compactFormatter = new Intl.NumberFormat("en-US", { notation: "compact", compactDisplay: "short" });
const dateFormatter = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric" });

const displayNameFor = (item: BlockedCreatorRow) => {
  const { nickname, handle } = getCreatorName(item.nickname, item.creatorName ?? item.username);
  return { nickname, handle, displayName: nickname ?? handle };
};

function OriginBadge({ origin, appliedBy }: { origin: string; appliedBy: AppliedBy | null }) {
  const label = origin === "csv" ? "CSV" : origin === "rule" ? "Rule" : "Manual";
  return (
    <RuleSourceTooltip appliedBy={appliedBy} action="blocked">
      <span className="text-xs text-tertiary">{label}</span>
    </RuleSourceTooltip>
  );
}

type BlockedCreatorsTableProps = Pick<
  UnboxTableProps<BlockedCreatorRow>,
  "rows" | "onClickRow" | "selection" | "pagination" | "hasActiveFilters"
> & {
  onClearSearch: () => void;
  onCreateRule: () => void;
  onManageAllCohorts: () => void;
  onUnblock: (target: { creatorOpenId: string; creatorName: string }) => void;
};

export function BlockedCreatorsTable({
  rows,
  onClickRow,
  selection,
  pagination,
  hasActiveFilters,
  onClearSearch,
  onCreateRule,
  onManageAllCohorts,
  onUnblock,
}: BlockedCreatorsTableProps) {
  const currency = useCurrency();

  const columns = useMemo<UnboxTableColumn<BlockedCreatorRow>[]>(() => {
    const gmvFormatter = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
      notation: "compact",
      compactDisplay: "short",
    });

    return [
      {
        id: "creator",
        label: "Creator",
        minWidth: "220px",
        render: (_value, item) => {
          const { nickname, handle, displayName } = displayNameFor(item);
          return (
            <div className="flex items-center gap-3">
              <Avatar
                size="sm"
                src={item.thumbnail ?? item.avatar ?? null}
                alt={displayName ?? undefined}
                initials={(nickname ?? item.creatorName ?? item.username ?? "?").charAt(0).toUpperCase()}
              />
              <div className="flex min-w-0 flex-col">
                <span className="truncate text-sm font-medium text-primary">{displayName ?? "—"}</span>
                {nickname && handle && <span className="truncate text-xs text-tertiary">{handle}</span>}
              </div>
            </div>
          );
        },
      },
      {
        id: "cohorts",
        label: "Cohorts",
        minWidth: "160px",
        render: (_value, item) =>
          item.creatorId ? (
            <CreatorCohortCellContent
              creatorId={item.creatorId}
              cohortId={item.cohortId}
              appliedBy={item.cohortAppliedBy}
              onManageAllCohorts={onManageAllCohorts}
            />
          ) : (
            <span className="text-xs text-quaternary">—</span>
          ),
      },
      {
        id: "safetyRisk",
        label: "Safety Risk",
        minWidth: "130px",
        render: (_value, item) => <CreatorSafetyStatusBadge safetyStatus={item.safetyStatus} />,
      },
      {
        id: "gmv",
        label: "GMV (90 days)",
        minWidth: "130px",
        render: (_value, item) => (
          <TrendNumberCellContent value={item.gmvAmount} trend={item.trendGmv} format={gmvFormatter.format} />
        ),
      },
      {
        id: "videos",
        label: "Videos 90d",
        minWidth: "100px",
        render: (_value, item) => (
          <span className="whitespace-nowrap text-sm font-medium text-primary">
            {item.ecVideoCount != null ? compactFormatter.format(item.ecVideoCount) : "—"}
          </span>
        ),
      },
      {
        id: "followers",
        label: "Followers",
        minWidth: "100px",
        render: (_value, item) => (
          <span className="whitespace-nowrap text-sm font-medium text-primary">
            {item.followerCount != null ? compactFormatter.format(item.followerCount) : "—"}
          </span>
        ),
      },
      {
        id: "addedVia",
        label: "Added via",
        minWidth: "100px",
        render: (_value, item) => <OriginBadge origin={item.origin} appliedBy={item.appliedBy} />,
      },
      {
        id: "dateAdded",
        label: "Date Added",
        minWidth: "120px",
        render: (_value, item) => (
          <span className="whitespace-nowrap text-sm font-medium text-primary">
            {dateFormatter.format(new Date(item.blockedAt))}
          </span>
        ),
      },
      {
        id: "actions",
        label: "",
        minWidth: "60px",
        render: (_value, item) => (
          <div className="flex items-center justify-end gap-1">
            <button
              type="button"
              title="Unblock creator"
              className="rounded p-1 text-quaternary transition-colors hover:bg-primary_hover hover:text-primary"
              onClick={(e) => {
                e.stopPropagation();
                onUnblock({
                  creatorOpenId: item.creatorOpenId,
                  creatorName: displayNameFor(item).displayName ?? "Creator",
                });
              }}
            >
              <ReverseLeft className="size-4" />
            </button>
          </div>
        ),
      },
    ];
  }, [currency, onManageAllCohorts, onUnblock]);

  return (
    <UnboxTable<BlockedCreatorRow>
      aria-label="Blocked Creators"
      columns={columns}
      rows={rows}
      rowId="creatorOpenId"
      onClickRow={onClickRow}
      selection={selection}
      pagination={pagination}
      hasActiveFilters={hasActiveFilters}
      emptyState={{
        // Ruler icon is correct for this as per design, don't change.
        icon: Ruler,
        title: "No blocked creators yet",
        description: "Block anyone manually from your creators list, or create a rule here.",
        action: { label: "Create Rule", icon: Plus, onClick: onCreateRule },
      }}
      filteredEmptyState={{ onClearFilters: onClearSearch }}
      size="sm"
      className="max-h-[60vh]"
    />
  );
}
